import { AudiusTrackSummary } from './audiusTypes';
import { buildHoldemBackendUrl } from '../config/runtime';

type JsonResponse<T> = {
  ok: boolean;
  data?: T;
  error?: {
    code?: string;
    message?: string;
  };
};

type BootstrapResponse = {
  appName: string;
  queue: AudiusTrackSummary[];
};

export class AudiusApiClient {
  private async fetchJson<T>(path: string, signal?: AbortSignal): Promise<T> {
    const response = await fetch(buildHoldemBackendUrl(path), {
      credentials: 'include',
      signal,
    });

    let payload: JsonResponse<T> | null = null;
    try {
      payload = await response.json() as JsonResponse<T>;
    } catch {
      payload = null;
    }

    if (!response.ok || !payload?.ok || payload.data === undefined) {
      throw new Error(payload?.error?.message || `Audio request failed: ${response.status}`);
    }

    return payload.data;
  }

  getBootstrap(signal?: AbortSignal): Promise<BootstrapResponse> {
    return this.fetchJson<BootstrapResponse>('/api/audio/audius/bootstrap', signal);
  }

  getTrending(limit = 10, signal?: AbortSignal): Promise<AudiusTrackSummary[]> {
    return this.fetchJson<AudiusTrackSummary[]>(
      `/api/audio/audius/trending?limit=${encodeURIComponent(String(limit))}`,
      signal,
    );
  }

  searchTracks(query: string, limit = 10, signal?: AbortSignal): Promise<AudiusTrackSummary[]> {
    return this.fetchJson<AudiusTrackSummary[]>(
      `/api/audio/audius/search?q=${encodeURIComponent(query)}&limit=${encodeURIComponent(String(limit))}`,
      signal,
    );
  }

  resolveTrack(url: string, signal?: AbortSignal): Promise<AudiusTrackSummary | null> {
    return this.fetchJson<AudiusTrackSummary | null>(
      `/api/audio/audius/resolve?url=${encodeURIComponent(url)}`,
      signal,
    );
  }

  getStreamUrl(trackId: string): string {
    return buildHoldemBackendUrl(`/api/audio/audius/stream/${encodeURIComponent(trackId)}`);
  }
}

export const audiusApiClient = new AudiusApiClient();
